/*
 *  CPU Utilisation chart for workspace detail page.
 *  Fetches historical CPU data from CloudWatch via the backend API on demand.
 *  Follows the same pattern as ScEnvironmentCostTable.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, observable, action, runInAction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { Icon } from 'semantic-ui-react';

import { httpApiGet } from '@amzn/base-ui/dist/helpers/api';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// Hourly points, so the axis names the day and the tooltip the hour. Browser local time, like the
// rest of the workspace detail page.
const axisFormat = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short' });
const tooltipFormat = new Intl.DateTimeFormat('en-AU', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function formatPoint(timestamp, format) {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? '' : format.format(date);
}

// expected props
// - envId (via prop)
// - scEnvironmentsStore (via injection)
class ScEnvironmentCpuChart extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.loading = true;
      this.error = null;
      this.cpuData = []; // Array of { timestamp, value }, hourly
    });
  }

  componentDidMount() {
    this.fetchCpuData();
  }

  get envsStore() {
    return this.props.scEnvironmentsStore;
  }

  getEnvironment() {
    return this.envsStore.getScEnvironment(this.props.envId);
  }

  fetchCpuData = async () => {
    try {
      runInAction(() => { this.loading = true; this.error = null; });
      const result = await httpApiGet(`api/workspaces/service-catalog/${this.props.envId}/cpu-utilization`);
      runInAction(() => {
        this.cpuData = result || [];
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.loading = false;
      });
    }
  };

  getChartOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: false },
      tooltips: {
        backgroundColor: '#1f2937',
        titleFontColor: '#f9fafb',
        bodyFontColor: '#f9fafb',
        xPadding: 12,
        yPadding: 12,
        cornerRadius: 8,
        callbacks: {
          title: (tooltipItems) => {
            const timestamp = tooltipItems[0]?.xLabel;
            return timestamp ? formatPoint(timestamp, tooltipFormat) : '';
          },
          label: (tooltipItem) => `${tooltipItem.yLabel.toFixed(1)}% CPU`,
        },
      },
      scales: {
        xAxes: [{
          scaleLabel: { display: true, labelString: 'Date', fontColor: '#374151' },
          gridLines: { display: false },
          ticks: {
            fontColor: '#6b7280',
            autoSkip: true,
            maxTicksLimit: 10,
            maxRotation: 0,
            callback: (value) => formatPoint(value, axisFormat),
          },
        }],
        yAxes: [{
          scaleLabel: { display: true, labelString: 'CPU Utilisation (%)', fontColor: '#374151' },
          ticks: {
            beginAtZero: true,
            min: 0,
            max: 100,
            fontColor: '#6b7280',
            callback: (value) => `${value}%`,
          },
          gridLines: { color: '#f3f4f6' },
        }],
      },
    };
  }

  getChartData() {
    // Filter out any entries with no value and sort by time
    const sorted = [...this.cpuData]
      .filter(d => d.value !== null && d.value !== undefined && d.timestamp)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    return {
      labels: sorted.map(d => d.timestamp),
      datasets: [{
        label: 'CPU Utilisation',
        data: sorted.map(d => d.value),
        borderColor: '#E61E2A', // RMIT red
        backgroundColor: 'rgba(230, 30, 42, 0.08)',
        fill: true,
        lineTension: 0,
        borderWidth: 1.5,
        // Hourly data is thousands of points for an older workspace; markers would be a solid band.
        pointRadius: 0,
        pointHitRadius: 4,
        pointHoverRadius: 4,
        pointBackgroundColor: '#E61E2A',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
      }],
    };
  }

  render() {
    if (this.loading) return <ProgressPlaceHolder />;
    if (this.error) return <ErrorBox error={this.error} className="p0" />;

    const chartData = this.getChartData();
    const hasData = chartData.labels.length > 0;

    return (
      <div className="workspace-cost-graph-container">
        <div className="workspace-cost-graph-info">
          <Icon name="info circle" style={iconStyle} />
          <span>Shows average hourly CPU utilisation since the workspace was created. Only hours the workspace was running are shown.</span>
        </div>
        {hasData ? (
          <div style={{ height: '300px' }}>
            <Line data={chartData} options={this.getChartOptions()} />
          </div>
        ) : (
          <div className="workspace-cost-graph-empty">
            <Icon name="microchip" />
            <div className="workspace-cost-graph-empty-title">No CPU Data Available</div>
            <div className="workspace-cost-graph-empty-text">
              CPU utilisation data will appear here once the workspace has been running.
            </div>
          </div>
        )}
      </div>
    );
  }
}

decorate(ScEnvironmentCpuChart, {
  loading: observable,
  error: observable,
  cpuData: observable,
  fetchCpuData: action,
});

export default inject('scEnvironmentsStore')(withRouter(observer(ScEnvironmentCpuChart)));
